import { useMemo, useState } from "react";
import api from "../../services/api";

const DOC_CONFIG = [
  { docType: "aadhaar", label: "Aadhaar Card", required: true },
  { docType: "pan", label: "PAN Card", required: true },
  { docType: "photo", label: "Passport Photo", required: true },
  { docType: "marksheet_10", label: "10th Marksheet", required: true },
  { docType: "marksheet_12", label: "12th Marksheet", required: true },
  { docType: "passport", label: "Passport", required: false },
  { docType: "driving_license", label: "Driving License", required: false },
  { docType: "college_id", label: "College ID", required: false }
];

const normalizeStatus = (s) => String(s || "").toLowerCase();
const statusView = (doc) => {
  if (!doc) return { key: "pending", label: "Pending", canUpload: true, color: "#64748b" };
  const st = normalizeStatus(doc.status);
  if (st === "rejected") return { key: "rejected", label: `Rejected${doc.review_comment ? ` - ${doc.review_comment}` : ""}`, canUpload: true, color: "#dc2626" };
  if (["approved", "available", "verified"].includes(st)) return { key: "verified", label: "Verified", canUpload: false, color: "#2563eb" };
  return { key: "uploaded", label: "Uploaded", canUpload: false, color: "#d97706" };
};

export default function EmployeeDocumentDashboard({ docs = [], onRefresh }) {
  const [busyDocType, setBusyDocType] = useState("");

  const latestByType = useMemo(() => {
    const map = {};
    (Array.isArray(docs) ? docs : []).forEach((d) => {
      const key = String(d.doc_type || "").toLowerCase();
      if (!key) return;
      const old = map[key];
      if (!old) {
        map[key] = d;
        return;
      }
      const oldTs = new Date(old.uploaded_at || 0).getTime();
      const newTs = new Date(d.uploaded_at || 0).getTime();
      if (newTs >= oldTs) map[key] = d;
    });
    return map;
  }, [docs]);

  const handleUpload = async (docType, file) => {
    if (!file) return;
    setBusyDocType(docType);
    try {
      const fd = new FormData();
      fd.append("docType", docType);
      fd.append("file", file);
      await api.uploadDocument(fd);
      onRefresh?.();
      alert("Document uploaded");
    } catch (e) {
      alert("Upload failed: " + e.message);
    } finally {
      setBusyDocType("");
    }
  };

  const renderRow = (cfg) => {
    const doc = latestByType[cfg.docType] || null;
    const st = statusView(doc);
    return (
      <div key={cfg.docType} style={{ border: "1px solid #e2e8f0", borderRadius: 10, padding: 12 }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center" }}>
          <div style={{ fontSize: 13, fontWeight: 700 }}>
            {cfg.label} {cfg.required ? <span style={{ color: "#dc2626", fontSize: 11 }}>*</span> : null}
          </div>
          <div style={{ fontSize: 12, color: st.color }}>{st.label}</div>
        </div>
        {doc?.doc_name ? <div style={{ marginTop: 6, fontSize: 11, color: "#64748b" }}>{doc.doc_name}</div> : null}
        <div style={{ display: "flex", gap: 8, marginTop: 8, alignItems: "center", flexWrap: "wrap" }}>
          {doc?.id ? (
            <button className="btn-o btn-s" onClick={async () => { try { await api.previewDocById(doc.id); } catch (e) { alert("Preview failed: " + e.message); } }}>
              Preview
            </button>
          ) : null}
          <input
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            disabled={!st.canUpload || busyDocType === cfg.docType}
            onChange={(e) => handleUpload(cfg.docType, e.target.files?.[0] || null)}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="card animate-in">
      <div className="card-header">
        <div className="card-title">Document Upload Dashboard</div>
        <div className="card-subtitle">Upload enabled only for Pending / Rejected</div>
      </div>
      <div className="card-body" style={{ display: "grid", gap: 10 }}>
        {DOC_CONFIG.map(renderRow)}
      </div>
    </div>
  );
}
